import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { AnnouncementsFeedDto } from '@/api/generated';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';

type Req = {
  lastReadAt: string;
};

type Return = {
  isMarking: boolean;
  markRead: (req: Req) => Promise<void>;
};

export const useMarkTeamAnnouncementsRead = (): Return => {
  const { teamAnnouncementsApi } = useApiClient();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      await teamAnnouncementsApi.markAnnouncementsRead({ lastReadAt: req.lastReadAt });
    },
    mutationKey: [QueryKeyEnum.TEAM_ANNOUNCEMENTS.MARK_READ],
    onSuccess: async () => {
      queryClient.setQueriesData<AnnouncementsFeedDto>(
        { queryKey: [QueryKeyEnum.TEAM_ANNOUNCEMENTS.FEED] },
        feed =>
          feed && {
            ...feed,
            items: feed.items.map(item => ({ ...item, isRead: true })),
            unreadCount: 0,
          },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.TEAM_ANNOUNCEMENTS.FEED] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.CHATS.ALL_STATUS] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.CHATS.ID] }),
      ]);
    },
  });

  return {
    isMarking: isPending,
    markRead: mutateAsync,
  };
};
